import { Link, Outlet, useLocation } from '@tanstack/react-router';
import { useCurrentPermissions } from '@/hooks/use-permissions';
import { visibleSetupItems } from '@/lib/setup-nav';
import { cn } from '@/lib/utils';

export default function SetupLayout(): React.ReactElement {
  const location = useLocation();
  const { permissions, isSystemAdmin } = useCurrentPermissions();

  // Hide nav items the user can't access. While permissions load, the hook
  // returns an empty set, so gated items stay hidden rather than flash and
  // snap away. System admins see all.
  const visibleItems = visibleSetupItems(permissions, isSystemAdmin);

  return (
    <div className="flex h-full">
      {/* Vertical Navigation */}
      <nav className="w-64 border-r bg-muted/30">
        <div className="p-6">
          <h1 className="text-xl font-semibold">Setup</h1>
          <p className="text-sm text-muted-foreground">Configure your practice</p>
        </div>
        <div className="px-3">
          {visibleItems.map((item) => {
            const isActive = location.pathname.startsWith(item.url);
            return (
              <Link
                key={item.url}
                to={item.url}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                  'hover:bg-accent hover:text-accent-foreground',
                  isActive
                    ? 'bg-accent text-accent-foreground font-medium'
                    : 'text-muted-foreground'
                )}
              >
                <item.icon className="h-4 w-4" />
                <div>
                  <div>{item.title}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Content Area */}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
